import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../api/api';
import {
    DollarSign,
    TrendingUp,
    TrendingDown,
    Activity,
    Clock,
    Users,
    BarChart3,
    Archive,
    RefreshCw,
    AlertTriangle,
    Calendar,
    Filter,
    Target,
    Zap,
    Sparkles,
    ArrowRight,
    ArrowUpRight,
    ArrowDownRight
} from 'lucide-react';
import PageShell from './ui/PageShell';
import PageHeader from './ui/PageHeader';
import StatCard from './ui/StatCard';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "./ui/select";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "./ui/card";
import { cn } from '../lib/utils';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "./ui/tooltip";
import { formatCurrency, formatDateTime } from '../lib/i18n';

export function SalesDashboard() {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    // Scoping State (Now route-backed in Sprint 9)
    const [searchParams, setSearchParams] = useSearchParams();

    // Canonical param derivation
    const rawTimeWindow = searchParams.get('timeWindow');
    const timeWindow = ['7d', '30d', '90d', 'all'].includes(rawTimeWindow) ? rawTimeWindow : '30d';
    const ownerId = searchParams.get('ownerId') || 'all';

    const updateDashboardParams = useCallback((updates) => {
        const nextParams = new URLSearchParams(searchParams);
        Object.entries(updates).forEach(([key, value]) => {
            if (value && value !== 'all' && key === 'ownerId') {
                nextParams.set(key, value);
            } else if (value && key === 'timeWindow' && value !== '30d') {
                nextParams.set(key, value);
            } else {
                nextParams.delete(key);
            }
        });
        setSearchParams(nextParams, { replace: true }); // Use replace to avoid history bloat on every filter toggle
    }, [searchParams, setSearchParams]);

    // URL Canonicalization (Sprint 9 Refinement)
    // If the raw URL params are invalid or non-canonical, update the URL bar to a clean state.
    useEffect(() => {
        if (rawTimeWindow && !['7d', '30d', '90d', 'all'].includes(rawTimeWindow)) {
            updateDashboardParams({ timeWindow: '30d' }); // repair invalid timeWindow
        }
    }, [rawTimeWindow, updateDashboardParams]);

    const [teamMembers, setTeamMembers] = useState([]);

    const [pipeline, setPipeline] = useState(null);
    const [aging, setAging] = useState(null);
    const [taskPulse, setTaskPulse] = useState(null);
    const [owners, setOwners] = useState(null);
    const [conversion, setConversion] = useState(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [sectionErrors, setSectionErrors] = useState({});
    const [lastUpdated, setLastUpdated] = useState(null);

    // Initial load for static team members
    useEffect(() => {
        api.get('/api/opportunities/workspace-members')
            .then(res => setTeamMembers(res.data.members || []))
            .catch(err => console.error("Failed to load workspace members for filter", err));
    }, []);

    const load = useCallback(async (force = false) => {
        setLoading(true);
        setError(false);
        setSectionErrors({});

        const queryParams = `?timeWindow=${timeWindow}&ownerId=${ownerId}${force ? '&force=true' : ''}`;
        const endpoints = [
            { id: 'pipeline', url: `/api/sales-reporting/pipeline${queryParams}` },
            { id: 'aging', url: `/api/sales-reporting/aging${queryParams}` },
            { id: 'taskPulse', url: `/api/sales-reporting/task-pulse${queryParams}` },
            { id: 'owners', url: `/api/sales-reporting/owners${queryParams}` },
            { id: 'conversion', url: `/api/sales-reporting/conversion${queryParams}` }
        ];

        const results = await Promise.allSettled(endpoints.map(e => api.get(e.url)));

        const setters = {
            pipeline: setPipeline,
            aging: setAging,
            taskPulse: setTaskPulse,
            owners: setOwners,
            conversion: setConversion
        };

        const newErrors = {};
        results.forEach((res, i) => {
            const id = endpoints[i].id;
            if (res.status === 'fulfilled') {
                setters[id](res.value.data);
            } else {
                console.error(`Failed to load ${id}:`, res.reason);
                newErrors[id] = true;
                // If core pipeline fails, we consider it a global error for UX
                if (id === 'pipeline') setError(true);
            }
        });

        setSectionErrors(newErrors);
        setLastUpdated(new Date());
        setLoading(false);
    }, [timeWindow, ownerId, setLoading, setError, setSectionErrors, setLastUpdated, setPipeline, setAging, setTaskPulse, setOwners, setConversion]);

    useEffect(() => {
        load();
    }, [searchParams, load]); // Re-load whenever URL scope changes

    const handleNav = (path) => navigate(path);

    const fmt = (v) => formatCurrency(v, 'USD', {}, i18n.language);

    const STAGE_ORDER = ['NEW', 'QUALIFYING', 'MEETING', 'PROPOSAL', 'NEGOTIATION', 'WON', 'LOST'];
    // Stage Colors (Mailwise Design System Palette)
    const STAGE_COLORS = {
        NEW: 'rgb(59, 130, 246)', // Blue
        QUALIFYING: 'rgb(79, 70, 229)', // Indigo
        MEETING: 'rgb(139, 92, 246)', // Violet
        PROPOSAL: 'rgb(249, 115, 22)', // Orange
        NEGOTIATION: 'rgb(234, 179, 8)', // Yellow
        WON: 'rgb(34, 197, 94)', // Green
        LOST: 'rgb(239, 68, 68)' // Red
    };

    const timeLabels = {
        '7d': t('salesDashboard.timeWindow.last7Days'),
        '30d': t('salesDashboard.timeWindow.last30Days'),
        '90d': t('salesDashboard.timeWindow.last90Days'),
        'all': t('salesDashboard.timeWindow.allTime')
    };

    if (loading && !pipeline) {
        return (
            <PageShell className="max-w-7xl mx-auto p-4 sm:p-8 space-y-8">
                <div className="space-y-8">
                    <div className="flex flex-col gap-2">
                        <Skeleton className="h-10 w-64 rounded-xl" />
                        <Skeleton className="h-4 w-96 rounded-lg opacity-50" />
                    </div>

                    <Skeleton className="h-20 w-full rounded-2xl" />

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[1, 2, 3, 4].map((i) => (
                            <Skeleton key={i} className="h-40 w-full rounded-3xl" />
                        ))}
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <Skeleton className="h-[400px] w-full rounded-3xl" />
                        <Skeleton className="h-[400px] w-full rounded-3xl" />
                    </div>
                </div>
            </PageShell>
        );
    }

    if (error && !pipeline) {
        return (
            <PageShell className="max-w-7xl mx-auto p-6 flex flex-col items-center justify-center h-96 text-center">
                <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">{t('salesDashboard.errors.loadTitle')}</h2>
                <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-md">{t('salesDashboard.errors.loadDescription')}</p>
                <button onClick={load} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition">
                    <RefreshCw className="w-4 h-4" /> {t('salesDashboard.actions.retryConnection')}
                </button>
            </PageShell>
        );
    }

    const maxStageValue = pipeline?.stageBreakdown?.reduce((m, s) => Math.max(m, s.totalValue), 1) || 1;
    const maxFunnelCount = conversion?.currentFunnel?.reduce((m, s) => Math.max(m, s.count), 1) || 1;

    return (
        <PageShell className={`space-y-6 max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 ${loading ? 'opacity-60 pointer-events-none transition-opacity' : 'transition-opacity'}`}>
            <div className="flex justify-between items-start">
                <PageHeader
                    title={t('salesDashboard.title')}
                    subtitle={t('salesDashboard.subtitle')}
                    icon={TrendingUp}
                />
            </div>

            {/* ─── Dashboard Scope Bar (Hardened Responsive Flex) ─── */}
            <div className="bg-card text-card-foreground rounded-2xl shadow-sm border border-border p-4 flex flex-col md:flex-row md:items-center justify-between gap-6 transition-all">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 flex-1">
                    <div className="flex items-center gap-2 text-xs font-black text-muted-foreground uppercase tracking-widest px-3 border-r border-border h-8">
                        <Filter className="w-3.5 h-3.5 text-primary" /> Scope
                    </div>

                    <div className="flex flex-wrap gap-4 w-full sm:w-auto">
                        <div className="min-w-[160px] flex-1 sm:flex-initial">
                            <Select
                                value={timeWindow}
                                onValueChange={(v) => updateDashboardParams({ timeWindow: v })}
                            >
                                <SelectTrigger className="h-10 bg-muted/30 border-border/50 hover:bg-muted/50 transition-colors rounded-xl font-bold text-sm">
                                    <div className="flex items-center gap-2">
                                        <Calendar className="w-4 h-4 text-muted-foreground" />
                                        <SelectValue placeholder={t('salesDashboard.filters.timeWindow')} />
                                    </div>
                                </SelectTrigger>
                                <SelectContent className="rounded-xl shadow-2xl border-border">
                                    <SelectItem value="7d" className="font-semibold px-4 py-2 hover:bg-muted transition-colors">{t('salesDashboard.timeWindow.last7Days')}</SelectItem>
                                    <SelectItem value="30d" className="font-semibold px-4 py-2 hover:bg-muted transition-colors">{t('salesDashboard.timeWindow.last30Days')}</SelectItem>
                                    <SelectItem value="90d" className="font-semibold px-4 py-2 hover:bg-muted transition-colors">{t('salesDashboard.timeWindow.last90Days')}</SelectItem>
                                    <SelectItem value="all" className="font-semibold px-4 py-2 hover:bg-muted transition-colors">{t('salesDashboard.timeWindow.allTimeLifetime')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="min-w-[200px] flex-1 sm:flex-initial">
                            <Select
                                value={ownerId}
                                onValueChange={(v) => updateDashboardParams({ ownerId: v })}
                            >
                                <SelectTrigger className="h-10 bg-muted/30 border-border/50 hover:bg-muted/50 transition-colors rounded-xl font-bold text-sm">
                                    <div className="flex items-center gap-2 truncate">
                                        <Users className="w-4 h-4 text-muted-foreground" />
                                        <SelectValue placeholder={t('salesDashboard.filters.projectOwner')} />
                                    </div>
                                </SelectTrigger>
                                <SelectContent className="rounded-xl shadow-2xl border-border max-h-[300px]">
                                    <SelectItem value="all" className="font-semibold px-4 py-2 hover:bg-muted transition-colors">{t('salesDashboard.filters.allOwners')}</SelectItem>
                                    <SelectItem value="unassigned" className="font-semibold px-4 py-2 hover:bg-muted transition-colors">{t('salesDashboard.filters.unassignedOnly')}</SelectItem>
                                    {teamMembers.map(m => (
                                        <SelectItem key={m._id} value={m._id} className="font-semibold px-4 py-2 hover:bg-muted transition-colors">
                                            {m.name || m.email}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>

                <div className="flex items-center justify-between sm:justify-end gap-6 border-t md:border-t-0 md:border-l border-border pt-4 md:pt-0 md:pl-6">
                    {lastUpdated && (
                            <div className="flex items-center gap-1.5 text-[10px] font-black text-muted-foreground uppercase tracking-tighter">
                            <Clock className="w-3 h-3 opacity-60" /> {formatDateTime(lastUpdated, { timeStyle: 'short' }, i18n.language)}
                        </div>
                    )}
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => load(true)}
                        className="h-8 gap-2 font-black text-[10px] uppercase tracking-widest text-muted-foreground hover:text-primary hover:bg-primary/5 rounded-lg border-dashed border-border/50 hover:border-primary/20 transition-all active:scale-95"
                    >
                        <RefreshCw className={cn("w-3.5 h-3.5 transition-transform", loading && "animate-spin text-primary")} />
                        {t('salesDashboard.actions.syncData')}
                    </Button>
                </div>
            </div>

            {/* ─── Metric Summary Section (Responsive Grid) ─── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="lg:col-span-1">
                    {sectionErrors.pipeline ? (
                        <SectionError title={t('salesDashboard.errors.pipelineTitle')} description={t('salesDashboard.errors.sectionDescription')} retryLabel={t('salesDashboard.actions.retrySection')} onRetry={load} />
                    ) : (
                        <StatCard
                            title={t('salesDashboard.cards.activeRevenuePipeline')}
                            value={fmt(pipeline?.openPipeline?.value)}
                            icon={DollarSign}
                            description={t('salesDashboard.cards.activeRevenuePipelineDescription', {
                                count: pipeline?.openPipeline?.count || 0,
                                risk: fmt(aging?.unifiedRisk?.value),
                            })}
                            variant="primary"
                            className="cursor-pointer h-full"
                            onClick={() => handleNav(`/opportunities?status=OPEN${ownerId !== 'all' ? `&ownerId=${ownerId}` : ''}`)}
                        />
                    )}
                </div>

                {sectionErrors.pipeline ? (
                    <div className="sm:col-span-1 lg:col-span-3 h-full"><SectionError title={t('salesDashboard.errors.historicalDataTitle')} description={t('salesDashboard.errors.sectionDescription')} retryLabel={t('salesDashboard.actions.retrySection')} onRetry={load} /></div>
                ) : (
                    <>
                        <StatCard
                            title={t('salesDashboard.cards.won', { window: timeLabels[timeWindow] })}
                            value={fmt(pipeline?.won?.value)}
                            icon={TrendingUp}
                            description={t('salesDashboard.cards.wonDescription', { count: pipeline?.won?.count || 0 })}
                            variant="success"
                            className="cursor-pointer"
                            onClick={() => handleNav(`/opportunities?status=WON&timeWindow=${timeWindow}${ownerId !== 'all' ? `&ownerId=${ownerId}` : ''}`)}
                        />
                        <StatCard
                            title={t('salesDashboard.cards.lost', { window: timeLabels[timeWindow] })}
                            value={fmt(pipeline?.lost?.value)}
                            icon={TrendingDown}
                            description={t('salesDashboard.cards.lostDescription', { count: pipeline?.lost?.count || 0 })}
                            variant="danger"
                            className="cursor-pointer"
                            onClick={() => handleNav(`/opportunities?status=LOST&timeWindow=${timeWindow}${ownerId !== 'all' ? `&ownerId=${ownerId}` : ''}`)}
                        />
                        <StatCard
                            title={t('salesDashboard.cards.archivedTotal')}
                            value={pipeline?.archived?.count || 0}
                            icon={Archive}
                            description={t('salesDashboard.cards.archivedTotalDescription')}
                            className="cursor-pointer"
                            onClick={() => handleNav(`/opportunities?status=ARCHIVED${ownerId !== 'all' ? `&ownerId=${ownerId}` : ''}`)}
                        />
                    </>
                )}
            </div>

            {/* ─── Stage Distribution + Pipeline Health (Responsive Reflow) ─── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                {/* Stage Distribution */}
                <Card className="rounded-3xl shadow-sm border-border overflow-hidden group hover:border-primary/20 transition-all duration-300">
                    <CardHeader className="pb-2">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 bg-primary/10 rounded-2xl border border-primary/20 text-primary">
                                <BarChart3 className="w-5 h-5" />
                            </div>
                            <div>
                                <CardTitle className="text-xl font-black tracking-tight">{t('salesDashboard.sections.pipelineDistribution')}</CardTitle>
                                <CardDescription className="text-xs font-bold text-muted-foreground uppercase tracking-widest opacity-70">{t('salesDashboard.sections.pipelineDistributionDescription')}</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {sectionErrors.pipeline ? (
                            <SectionError title={t('salesDashboard.errors.stageDataTitle')} description={t('salesDashboard.errors.sectionDescription')} retryLabel={t('salesDashboard.actions.retrySection')} onRetry={load} />
                        ) : (
                            <div className="space-y-6 mt-4">
                                {STAGE_ORDER.slice(0, 5).map(stageName => {
                                    const s = pipeline?.stageBreakdown?.find(b => b._id === stageName) || { totalValue: 0, count: 0 };
                                    const pct = maxStageValue > 1 ? Math.round((s.totalValue / maxStageValue) * 100) : 0;
                                    const isEmpty = s.count === 0;

                                    return (
                                        <div
                                            key={stageName}
                                            onClick={!isEmpty ? () => handleNav(`/opportunities?status=OPEN&stage=${stageName}${ownerId !== 'all' ? `&ownerId=${ownerId}` : ''}`) : undefined}
                                            className={cn(
                                                "transition-all rounded-2xl px-3 py-2 -mx-3",
                                                isEmpty ? "opacity-50 grayscale" : "opacity-100 cursor-pointer hover:bg-muted/50"
                                            )}
                                        >
                                            <div className="flex justify-between items-end mb-2">
                                                <span className={cn(
                                                    "text-sm font-black tracking-tight uppercase",
                                                    isEmpty ? "text-muted-foreground" : "text-foreground"
                                                )}>{stageName}</span>
                                                <div className="text-right">
                                                    <span className={cn(
                                                        "text-base font-black tracking-tight",
                                                        isEmpty ? "text-muted-foreground" : "text-foreground"
                                                    )}>{fmt(s.totalValue)}</span>
                                                    <span className="text-[10px] font-black text-muted-foreground ml-2 opacity-60 uppercase tracking-widest">{t('salesDashboard.common.dealsCount', { count: s.count })}</span>
                                                </div>
                                            </div>
                                            <div className="h-3 bg-muted/50 rounded-full overflow-hidden flex border border-border/30">
                                                <div
                                                    className="h-full rounded-full transition-all duration-1000 ease-in-out shadow-[0_0_10px_rgba(0,0,0,0.1)]"
                                                    style={{ width: `${pct}%`, backgroundColor: isEmpty ? undefined : STAGE_COLORS[stageName] }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Operational Health Intelligence */}
                <Card className="rounded-3xl shadow-sm border-border overflow-hidden group hover:border-primary/20 transition-all duration-300">
                    <CardHeader className="pb-2">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 bg-primary/10 rounded-2xl border border-primary/20 text-primary">
                                <Activity className="w-5 h-5" />
                            </div>
                            <div>
                                <CardTitle className="text-xl font-black tracking-tight">{t('salesDashboard.sections.operationalHealth')}</CardTitle>
                                <CardDescription className="text-xs font-bold text-muted-foreground uppercase tracking-widest opacity-70">{t('salesDashboard.sections.operationalHealthDescription')}</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                    {sectionErrors.aging ? (
                        <SectionError title={t('salesDashboard.errors.healthIntelligenceTitle')} description={t('salesDashboard.errors.sectionDescription')} retryLabel={t('salesDashboard.actions.retrySection')} onRetry={load} />
                    ) : (
                        <>
                            {/* Health Status Header */}
                            <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100 dark:border-gray-800">
                                <TooltipProvider>
                                    <div className="flex items-center gap-3">
                                        <span className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Calculated Status:</span>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <div className="cursor-help transition-transform hover:scale-105 active:scale-95">
                                                    <HealthBadge
                                                        totalValue={pipeline?.openPipeline?.value || 0}
                                                        openCount={pipeline?.openPipeline?.count || 0}
                                                        riskValue={aging?.unifiedRisk?.value || 0}
                                                        severeValue={(aging?.overdue?.tiers?.find(t => t._id === 'Severe')?.value || 0) + (aging?.stale?.tiers?.find(t => t._id === 'Deeply Stale')?.value || 0)}
                                                    />
                                                </div>
                                            </TooltipTrigger>
                                            <TooltipContent className="max-w-[320px] p-3 text-xs leading-relaxed border-indigo-100 dark:border-indigo-900 shadow-xl">
                                                <p className="font-bold mb-1 text-indigo-600 dark:text-indigo-400 uppercase tracking-tighter">Health Heuristics (Unified Risk):</p>
                                                <ul className="space-y-1 list-disc pl-4 opacity-90">
                                                    <li><span className="font-bold text-red-500">CRITICAL</span>: Unique items &gt; 30% OR Severe Risk &gt; 15% (Min 3 deals + $10k volume)</li>
                                                    <li><span className="font-bold text-orange-500">AT RISK</span>: Unique items &gt; 15% OR Severe Risk &gt; 5%</li>
                                                    <li><span className="font-bold text-amber-500">WATCH</span>: Unique items &gt; 5%</li>
                                                    <li className="list-none mt-2 pt-2 border-t border-border opacity-60 italic">Denominator: Total Open Pipeline Value</li>
                                                </ul>
                                            </TooltipContent>
                                        </Tooltip>
                                    </div>
                                </TooltipProvider>
                                <div className="text-[10px] text-gray-400 italic">Heuristic-based assessment</div>
                            </div>

                            {/* Severity Alerts */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                                {/* Overdue Alert */}
                                <div className="space-y-3">
                                    <AlertBox
                                        title={t('salesDashboard.alerts.overdueClose')}
                                        count={aging?.overdue?.count || 0}
                                        sub={t('salesDashboard.alerts.exposure', { value: fmt(aging?.overdue?.value) })}
                                        danger={aging?.overdue?.count > 0}
                                        onClick={() => handleNav(`/opportunities?status=OPEN&isOverdue=true${ownerId !== 'all' ? `&ownerId=${ownerId}` : ''}`)}
                                    />
                                    <div className="grid grid-cols-3 gap-2">
                                        {['Mild', 'Significant', 'Severe'].map(tier => {
                                            const t = aging?.overdue?.tiers?.find(x => x._id === tier) || { count: 0, value: 0 };
                                            const colors = tier === 'Mild' ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/10 dark:text-amber-400 border-amber-100 dark:border-amber-800/30' :
                                                           tier === 'Significant' ? 'bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400 border-orange-200 dark:border-orange-800/50' :
                                                           'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400 border-red-200 dark:border-red-800/50';
                                            return (
                                                <div key={tier} className={`p-2 rounded-lg border flex flex-col items-center justify-center text-center ${colors} ${t.count === 0 ? 'opacity-30' : ''}`}>
                                                    <span className="text-[8px] font-bold uppercase opacity-80">{tier}</span>
                                                    <span className="text-xs font-black">{t.count}</span>
                                                    <span className="text-[9px] font-bold opacity-70">{fmt(t.value)}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Stale Alert */}
                                <div className="space-y-3">
                                    <AlertBox
                                        title={t('salesDashboard.alerts.staleDeals')}
                                        count={aging?.stale?.count || 0}
                                        sub={t('salesDashboard.alerts.exposure', { value: fmt(aging?.stale?.value) })}
                                        danger={aging?.stale?.count > 0}
                                        onClick={() => handleNav(`/opportunities?status=OPEN&isStale=true${ownerId !== 'all' ? `&ownerId=${ownerId}` : ''}`)}
                                    />
                                    <div className="grid grid-cols-2 gap-2">
                                        {['Stagnant', 'Deeply Stale'].map(tier => {
                                            const t = aging?.stale?.tiers?.find(x => x._id === tier) || { count: 0, value: 0 };
                                            const colors = tier === 'Stagnant' ? 'bg-orange-50 text-orange-700 dark:bg-orange-900/10 dark:text-orange-400 border-orange-100 dark:border-orange-800/30' :
                                                           'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400 border-red-200 dark:border-red-800/50';
                                            return (
                                                <div key={tier} className={`p-2 rounded-lg border flex flex-col items-center justify-center text-center ${colors} ${t.count === 0 ? 'opacity-30' : ''}`}>
                                                    <span className="text-[8px] font-bold uppercase opacity-80">{tier}</span>
                                                    <span className="text-xs font-black">{t.count}</span>
                                                    <span className="text-[9px] font-bold opacity-70">{fmt(t.value)}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            {/* Risk Concentration */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-6 border-t border-gray-100 dark:border-gray-800">
                                <div>
                                    <h4 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                                        <BarChart3 className="w-3.5 h-3.5" /> Stage Risk Concentration
                                    </h4>
                                    <div className="space-y-3">
                                        {(!aging?.concentration?.byStage || aging.concentration.byStage.length === 0) ? (
                                            <p className="text-xs text-gray-400 italic">No significant stage-level risk concentration.</p>
                                        ) : (
                                            aging.concentration.byStage.sort((a, b) => b.value - a.value).slice(0, 4).map(c => (
                                                <div key={c.stage} className="space-y-1">
                                                    <div className="flex justify-between text-[11px] font-semibold">
                                                        <span className="text-gray-600 dark:text-gray-400">{c.stage}</span>
                                                        <span className="text-red-600 dark:text-red-400">{fmt(c.value)}</span>
                                                    </div>
                                                    <div className="h-1.5 w-full bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                                                        <div className="h-full bg-red-400 dark:bg-red-500 rounded-full" style={{ width: `${Math.min(100, (c.value / (aging.unifiedRisk.value || 1)) * 100)}%` }} />
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <h4 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                                        <Users className="w-3.5 h-3.5" /> Owner Risk Concentration
                                    </h4>
                                    <div className="space-y-3">
                                        {(!aging?.concentration?.byOwner || aging.concentration.byOwner.length === 0) ? (
                                            <p className="text-xs text-gray-400 italic">Risk is evenly distributed across owners.</p>
                                        ) : (
                                            aging.concentration.byOwner.slice(0, 4).map(c => (
                                                <div key={c.ownerId} className="space-y-1">
                                                    <div className="flex justify-between text-[11px] font-semibold">
                                                        <span className="text-gray-600 dark:text-gray-400 truncate max-w-[120px]">{c.name}</span>
                                                        <span className="text-red-600 dark:text-red-400">{fmt(c.value)}</span>
                                                    </div>
                                                    <div className="h-1.5 w-full bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                                                        <div className="h-full bg-orange-400 dark:bg-orange-500 rounded-full" style={{ width: `${Math.min(100, (c.value / (aging.unifiedRisk.value || 1)) * 100)}%` }} />
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Aging Buckets (Counts + Value) */}
                            <div className="pt-6 border-t border-gray-100 dark:border-gray-800">
                                <h4 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                                    <Clock className="w-3.5 h-3.5" /> Pipeline Aging (Velocity Analysis)
                                </h4>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                    {(!aging?.agingBuckets || aging.agingBuckets.length === 0) ? (
                                        <p className="text-sm text-gray-400 italic">No aging data available.</p>
                                    ) : (
                                        aging.agingBuckets.map(b => {
                                            const isOld = b._id === '30+ days' || b._id === '15-30 days';
                                            return (
                                                <div key={b._id} className={`p-3 rounded-xl border border-gray-100 dark:border-gray-800 transition-all ${isOld ? 'bg-orange-50/30 dark:bg-orange-900/5 border-orange-100/50 dark:border-orange-800/30' : 'bg-gray-50/30 dark:bg-gray-800/20'}`}>
                                                    <p className={`text-[9px] font-bold uppercase tracking-wider mb-1 ${isOld ? 'text-orange-600 dark:text-orange-400' : 'text-gray-500 dark:text-gray-400'}`}>{b._id}</p>
                                                    <p className="text-lg font-black text-gray-900 dark:text-gray-100">{b.count}</p>
                                                    <p className="text-[10px] font-medium text-gray-400 mt-1">{fmt(b.value)}</p>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>
        </div>

            {/* ─── Task Pulse + Conversion (Responsive Grid) ─── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                {/* Task Pulse */}
                <Card className="rounded-3xl shadow-sm border-border overflow-hidden group hover:border-primary/20 transition-all duration-300">
                    <CardHeader className="pb-2">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 bg-primary/10 rounded-2xl border border-primary/20 text-primary">
                                <Clock className="w-5 h-5" />
                            </div>
                            <div>
                                <CardTitle className="text-xl font-black tracking-tight">Current Execution Pulse</CardTitle>
                                <CardDescription className="text-xs font-bold text-muted-foreground uppercase tracking-widest opacity-70">Operational task execution metrics</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {sectionErrors.taskPulse ? (
                            <SectionError title={t('salesDashboard.errors.taskPulseTitle')} description={t('salesDashboard.errors.sectionDescription')} retryLabel={t('salesDashboard.actions.retrySection')} onRetry={load} />
                        ) : (
                            <>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8 mt-4">
                                    <PulseStat onClick={() => handleNav(`/tasks?filterMode=open${ownerId !== 'all' ? `&assignedTo=${ownerId}` : ''}`)} label={t('salesDashboard.pulse.pending')} value={taskPulse?.open || 0} />
                                    <PulseStat onClick={() => handleNav(`/tasks?filterMode=overdue${ownerId !== 'all' ? `&assignedTo=${ownerId}` : ''}`)} label={t('salesDashboard.pulse.overdue')} value={taskPulse?.overdue || 0} danger={taskPulse?.overdue > 0} />
                                    <PulseStat onClick={() => handleNav(`/tasks?filterMode=completed${ownerId !== 'all' ? `&assignedTo=${ownerId}` : ''}`)} label={t('salesDashboard.pulse.doneToday')} value={taskPulse?.completedToday || 0} />
                                    <PulseStat onClick={() => handleNav(`/tasks?filterMode=completed${ownerId !== 'all' ? `&assignedTo=${ownerId}` : ''}`)} label={t('salesDashboard.pulse.doneWeek')} value={taskPulse?.completedThisWeek || 0} />
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 pt-6 border-t border-border/50">
                                    <div>
                                        <h4 className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-4 flex items-center gap-2">
                                            <Target className="w-3 h-3" /> Priority Breakdown
                                        </h4>
                                        <div className="flex flex-wrap gap-2">
                                            {(!taskPulse?.byPriority || taskPulse.byPriority.length === 0) ? (
                                                <span className="text-sm text-muted-foreground italic">No pending tasks</span>
                                            ) : (
                                                taskPulse.byPriority.map(p => (
                                                    <Badge key={p._id} variant="secondary" className="bg-muted/50 border-border/50 font-black text-[10px] uppercase tracking-tighter px-2 py-1 text-foreground">
                                                        {p._id} <span className="ml-1.5 text-primary">{p.count}</span>
                                                    </Badge>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <h4 className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-4 flex items-center gap-2">
                                            <Sparkles className="w-3 h-3 text-amber-500" /> Intelligence Engine
                                        </h4>
                                        <div className="text-sm font-bold text-foreground flex items-baseline gap-2">
                                            <span className="text-2xl font-black text-primary">{taskPulse?.playbookGenerated || 0}</span>
                                            <span className="opacity-60 uppercase tracking-tighter text-[10px]">Playbook Automations active</span>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </CardContent>
                </Card>

            {/* Conversion & Funnel */}
            <Card className="rounded-3xl shadow-sm border-border overflow-hidden group hover:border-primary/20 transition-all duration-300">
                <CardHeader className="pb-2">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-primary/10 rounded-2xl border border-primary/20 text-primary">
                            <Filter className="w-5 h-5" />
                        </div>
                        <div>
                            <CardTitle className="text-xl font-black tracking-tight">Funnel & Conversion</CardTitle>
                            <CardDescription className="text-xs font-bold text-muted-foreground uppercase tracking-widest opacity-70">Historical Movement and Velocity</CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {sectionErrors.conversion ? (
                        <SectionError title={t('salesDashboard.errors.conversionTitle')} description={t('salesDashboard.errors.sectionDescription')} retryLabel={t('salesDashboard.actions.retrySection')} onRetry={load} />
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-4">
                            <div>
                                <h4 className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-5 flex items-center gap-2">
                                    <BarChart3 className="w-3.5 h-3.5" /> Pipeline Shape
                                </h4>
                                <div className="space-y-4">
                                    {(conversion?.currentFunnel || []).map(s => {
                                        const isEmpty = s.count === 0;
                                        return (
                                            <div key={s.stage} className={cn("space-y-1.5", isEmpty ? "opacity-30" : "opacity-100")}>
                                                <div className="flex justify-between text-[11px] font-black items-baseline">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-muted-foreground w-16 truncate uppercase tracking-tighter">{s.stage}</span>
                                                        <span className="text-foreground text-sm">{s.count}</span>
                                                    </div>
                                                    <div className="flex flex-col items-end">
                                                        <span className="text-foreground">{fmt(s.value)}</span>
                                                        <span className="text-[9px] text-muted-foreground font-bold tracking-tighter uppercase opacity-60">{Math.round(s.sharePct)}% Share</span>
                                                    </div>
                                                </div>
                                                <div className="h-2 w-full bg-muted/30 rounded-full overflow-hidden border border-border/20">
                                                    <div className="h-full transition-all duration-1000 ease-in-out" style={{ width: `${Math.min(100, (s.count / maxFunnelCount) * 100)}%`, backgroundColor: STAGE_COLORS[s.stage] || 'var(--muted)' }} />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="flex flex-col">
                                <h4 className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-5 flex items-center gap-2">
                                    <RefreshCw className="w-3.5 h-3.5" /> Stage Transitions
                                </h4>

                                <div className="flex-1 space-y-3 max-h-[360px] overflow-y-auto pr-2 custom-scrollbar">
                                    {(!conversion?.transitions || conversion.transitions.length === 0) ? (
                                        <div className="h-full flex flex-col items-center justify-center text-center p-8 border-2 border-dashed border-border/50 rounded-2xl opacity-40">
                                            <Activity className="w-10 h-10 mb-2" />
                                            <p className="text-[10px] font-bold uppercase tracking-widest">No Movements Recorded</p>
                                        </div>
                                    ) : (
                                        conversion.transitions.slice(0, 8).map((t, idx) => {
                                            const typeColor = t.type === 'FORWARD' ? 'text-emerald-500' :
                                                            t.type === 'DROPPED' ? 'text-destructive font-black' :
                                                            'text-amber-500';
                                            return (
                                                <div key={idx} className="group p-3 rounded-2xl border border-border/50 bg-card hover:bg-muted/50 transition-all shadow-sm flex items-center justify-between gap-4">
                                                    <div className="flex items-center gap-3 min-w-0">
                                                        <div className={cn("p-2 rounded-xl shrink-0", t.type === 'FORWARD' ? 'bg-emerald-500/10' : 'bg-muted')}>
                                                            <ArrowRight className={cn("w-3.5 h-3.5", typeColor)} />
                                                        </div>
                                                        <div className="flex flex-col min-w-0">
                                                            <div className="flex items-center gap-1.5 text-[11px] font-black tracking-tight truncate">
                                                                <span className="text-muted-foreground opacity-60 uppercase">{t.from}</span>
                                                                <ArrowRight className="w-3 h-3 text-muted-foreground/30" />
                                                                <span className="text-foreground uppercase">{t.to}</span>
                                                            </div>
                                                            <span className={cn("text-[9px] font-black uppercase tracking-widest opacity-80", typeColor)}>
                                                                {t.type} {t.type === 'DROPPED' ? 'LEAKAGE' : t.type === 'BACKWARD' ? 'REGRESSION' : 'PROGRESSION'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="text-right shrink-0">
                                                        <span className="text-base font-black text-foreground leading-none">{t.count}</span>
                                                        <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-widest opacity-60">Deals</p>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>

        {/* ─── Owner Performance (Full Design Alignment) ─── */}
        <Card className="rounded-3xl shadow-md border-border overflow-hidden group hover:border-primary/10 transition-all duration-300">
            <CardHeader className="border-b border-border/50 bg-muted/20 pb-8">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                    <div>
                        <div className="flex items-center gap-3 mb-1">
                            <div className="p-2.5 bg-primary/10 rounded-2xl border border-primary/20 text-primary">
                                <Users className="w-5 h-5 transition-transform group-hover:scale-110" />
                            </div>
                            <CardTitle className="text-2xl font-black tracking-tight">Team Performance Intelligence</CardTitle>
                        </div>
                        <CardDescription className="text-xs font-black text-muted-foreground uppercase tracking-widest pl-12 opacity-70">
                            {t('salesDashboard.sections.teamPerformanceDescription')}
                        </CardDescription>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-8">
                    {sectionErrors.owners ? (
                        <div className="col-span-3"><SectionError title={t('salesDashboard.errors.spotlightsTitle')} description={t('salesDashboard.errors.sectionDescription')} retryLabel={t('salesDashboard.actions.retrySection')} onRetry={() => load()} /></div>
                    ) : (
                        <>
                            <SpotlightCard
                                icon={Zap}
                                title={t('salesDashboard.spotlights.topCloser')}
                                name={owners?.spotlights?.topCloser?.name}
                                value={fmt(owners?.spotlights?.topCloser?.value)}
                                sub={t('salesDashboard.spotlights.topCloserSub')}
                                color="text-emerald-500"
                                bg="bg-emerald-500/10"
                            />
                            <SpotlightCard
                                icon={Target}
                                title={t('salesDashboard.spotlights.pipelineGiant')}
                                name={owners?.spotlights?.pipelineGiant?.name}
                                value={fmt(owners?.spotlights?.pipelineGiant?.value)}
                                sub={t('salesDashboard.spotlights.pipelineGiantSub')}
                                color="text-primary"
                                bg="bg-primary/10"
                            />
                            <SpotlightCard
                                icon={Activity}
                                title={t('salesDashboard.spotlights.executionLeader')}
                                name={owners?.spotlights?.executionLeader?.name}
                                value={`${Math.round(owners?.spotlights?.executionLeader?.value || 0)}%`}
                                sub={t('salesDashboard.spotlights.executionLeaderSub')}
                                color="text-amber-500"
                                bg="bg-amber-500/10"
                            />
                        </>
                    )}
                </div>
            </CardHeader>

            <CardContent className="p-0 border-t border-border/50">
                {sectionErrors.owners ? (
                    <div className="p-12 text-center"><SectionError title={t('salesDashboard.errors.ownerMetricsTitle')} description={t('salesDashboard.errors.sectionDescription')} retryLabel={t('salesDashboard.actions.retrySection')} onRetry={() => load()} /></div>
                ) : (!owners?.owners || owners.owners.length === 0) ? (
                    <div className="p-16 text-center bg-muted/5">
                        <Users className="w-12 h-12 text-muted/30 mx-auto mb-4" />
                        <p className="text-lg font-black text-muted-foreground">{t('salesDashboard.empty.noPerformanceData')}</p>
                    </div>
                ) : (
                    <div className="responsive-table-wrap">
                        <table className="w-full text-sm text-left border-collapse min-w-[1100px]">
                            <thead>
                                <tr className="bg-muted/30">
                                    <th className="px-6 py-5 font-black text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border w-[280px]">{t('salesDashboard.table.assigneeIntelligence')}</th>
                                    <th colSpan="4" className="px-6 py-2 text-center border-b border-l border-border bg-muted/10">
                                        <span className="text-[9px] font-black text-muted-foreground uppercase tracking-widest opacity-60">{t('salesDashboard.table.revenuePortfolio')}</span>
                                    </th>
                                    <th colSpan="2" className="px-6 py-2 text-center border-b border-l border-border bg-destructive/5">
                                        <span className="text-[9px] font-black text-destructive/70 uppercase tracking-widest opacity-80">{t('salesDashboard.table.healthShield')}</span>
                                    </th>
                                    <th colSpan="3" className="px-6 py-2 text-center border-b border-l border-border bg-muted/10">
                                        <span className="text-[9px] font-black text-muted-foreground uppercase tracking-widest opacity-60">{t('salesDashboard.table.taskExecution')}</span>
                                    </th>
                                </tr>
                                <tr className="bg-muted/10">
                                    <th className="px-6 py-4 border-b border-border"></th>
                                    <th className="px-4 py-4 text-right text-[10px] font-black text-muted-foreground uppercase tracking-widest border-b border-l border-border">{t('salesDashboard.table.currentValue')}</th>
                                    <th className="px-4 py-4 text-right text-[10px] font-black text-muted-foreground uppercase tracking-widest border-b border-border">{t('salesDashboard.table.open')}</th>
                                    <th className="px-4 py-4 text-right text-[10px] font-black text-muted-foreground uppercase tracking-widest border-b border-border">{t('salesDashboard.table.won')}</th>
                                    <th className="px-4 py-4 text-right text-[10px] font-black text-muted-foreground uppercase tracking-widest border-b border-border">{t('salesDashboard.table.lost')}</th>
                                    <th className="px-4 py-4 text-right text-[10px] font-black text-destructive uppercase tracking-widest border-b border-l border-border">{t('salesDashboard.table.atRisk')}</th>
                                    <th className="px-4 py-4 text-right text-[10px] font-black text-destructive uppercase tracking-widest border-b border-border">{t('salesDashboard.table.exposure')}</th>
                                    <th className="px-4 py-4 text-right text-[10px] font-black text-muted-foreground uppercase tracking-widest border-b border-l border-border">{t('salesDashboard.table.pending')}</th>
                                    <th className="px-4 py-4 text-right text-[10px] font-black text-muted-foreground uppercase tracking-widest border-b border-border">{t('salesDashboard.table.overdue')}</th>
                                    <th className="px-4 py-4 text-right text-[10px] font-black text-muted-foreground uppercase tracking-widest border-b border-border">{t('salesDashboard.table.success')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border/50 bg-card">
                                {(owners?.owners || []).map((o, i) => {
                                    const isAboveWonAvg = o.wonValue > (owners?.benchmarks?.wonValueAvg || 0);
                                    const isBelowRiskAvg = o.atRiskPct < (owners?.benchmarks?.atRiskPctAvg || 0);
                                    const isAboveExecAvg = o.executionRate > (owners?.benchmarks?.executionRateAvg || 0);

                                    return (
                                        <tr key={i} className="hover:bg-muted/30 transition-colors group">
                                            <td className="px-6 py-6">
                                                <div className="flex flex-col">
                                                    <div
                                                        className="font-black text-sm text-foreground hover:text-primary cursor-pointer flex items-center gap-2 transition-colors uppercase tracking-tight"
                                                        onClick={() => handleNav(`/opportunities?ownerId=${o.ownerId || 'unassigned'}`)}
                                                    >
                                                        {o.email === 'Unassigned' || o.email === 'Unknown' ? <span className="text-muted-foreground italic font-bold">{t('salesDashboard.common.unassigned')}</span> : o.email}
                                                        <ArrowUpRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity text-primary" />
                                                    </div>
                                                    <div className="flex flex-wrap gap-1.5 mt-2">
                                                        {(o.signals || []).map(s => (
                                                            <span key={s} className="text-[8px] font-black tracking-widest px-2 py-0.5 rounded-lg uppercase bg-muted text-muted-foreground border border-border/50">
                                                                {s.replace('_', ' ')}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 text-right border-l border-border/50">
                                                <div className="flex flex-col items-end">
                                                    <span className="font-black text-foreground">{fmt(o.openValue)}</span>
                                                    <span className="text-[10px] font-black text-muted-foreground opacity-60 uppercase">{t('salesDashboard.common.dealsCount', { count: o.openCount })}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 text-right">
                                                <div className="flex flex-col items-end">
                                                    <div className="flex items-center gap-1.5">
                                                        {o.wonValue > 0 && (isAboveWonAvg ? <ArrowUpRight className="w-3 h-3 text-emerald-500" /> : <ArrowDownRight className="w-3 h-3 text-muted-foreground/30" />)}
                                                        <span className={cn("font-black", isAboveWonAvg ? 'text-emerald-500' : 'text-foreground')}>{fmt(o.wonValue)}</span>
                                                    </div>
                                                    <span className="text-[10px] font-black text-muted-foreground opacity-60 uppercase">{t('salesDashboard.common.wonCount', { count: o.wonCount })}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 text-right text-muted-foreground font-black opacity-60">{o.lostCount}</td>
                                            <td className="px-4 py-4 text-right border-l border-border/50 font-black text-destructive">
                                                {fmt(o.unifiedRiskValue)}
                                            </td>
                                            <td className="px-4 py-4 text-right">
                                                <div className="flex flex-col items-end">
                                                    <span className={cn("font-black", o.atRiskPct > 30 ? 'text-destructive' : isBelowRiskAvg ? 'text-emerald-500' : 'text-foreground')}>
                                                        {Math.round(o.atRiskPct)}%
                                                    </span>
                                                    <span className="text-[9px] font-black text-muted-foreground uppercase opacity-60">{t('salesDashboard.table.exposure')}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 text-right border-l border-border/50 text-muted-foreground font-black opacity-60">
                                                {o.openTasks}
                                            </td>
                                            <td className="px-4 py-4 text-right cursor-pointer hover:bg-destructive/5 transition-colors" onClick={() => handleNav(`/tasks?assignedTo=${o.ownerId || 'unassigned'}`)}>
                                                {o.overdueTasks > 0 ? (
                                                    <span className="inline-flex items-center justify-center px-2 py-1 text-[10px] font-black bg-destructive text-destructive-foreground rounded-lg shadow-sm">
                                                        {o.overdueTasks}
                                                    </span>
                                                ) : (
                                                    <span className="text-muted-foreground/30 font-black">0</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-4 text-right">
                                                <div className="flex flex-col items-end">
                                                    <div className="flex items-center gap-1">
                                                        {o.executionRate > 0 && (isAboveExecAvg ? <TrendingUp className="w-3 h-3 text-emerald-500" /> : <TrendingDown className="w-3 h-3 text-amber-500" />)}
                                                        <span className={cn("font-black", isAboveExecAvg ? 'text-emerald-500' : 'text-foreground')}>{Math.round(o.executionRate)}%</span>
                                                    </div>
                                                    <span className="text-[9px] font-black text-muted-foreground uppercase opacity-60">{t('salesDashboard.common.doneCount', { count: o.completedTasks })}</span>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </CardContent>
        </Card>

        <div className="text-center text-[10px] font-black text-muted-foreground/30 uppercase tracking-[0.2em] pt-12 pb-6">
            {t('salesDashboard.footer')}
        </div>
    </PageShell>
);
}

function HealthBadge({ totalValue, riskValue, severeValue, openCount }) {
    const ratio = totalValue > 0 ? (riskValue / totalValue) * 100 : 0;
    const severeRatio = totalValue > 0 ? (severeValue / totalValue) * 100 : 0;

    const hasSample = openCount >= 3 && totalValue >= 10000;

    let status = {
        label: 'HEALTHY',
        color: 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20'
    };

    if (hasSample) {
        if (ratio > 30 || severeRatio > 15) {
            status = {
                label: 'CRITICAL',
                color: 'text-destructive bg-destructive/10 border-destructive/20 animate-pulse'
            };
        } else if (ratio > 15 || severeRatio > 5) {
            status = {
                label: 'AT RISK',
                color: 'text-amber-500 bg-amber-500/10 border-amber-500/20'
            };
        }
    }

    if (status.label === 'HEALTHY' && ratio > 5) {
        status = {
            label: 'WATCH',
            color: 'text-primary bg-primary/10 border-primary/20'
        };
    }

    return (
        <Badge variant="outline" className={cn("font-black tracking-tighter px-2.5 py-0.5 rounded-lg border uppercase shadow-sm", status.color)}>
            {status.label}
        </Badge>
    );
}

function AlertBox({ title, count, sub, danger, onClick }) {
    const isActionable = onClick && count > 0;
    return (
        <div
            onClick={isActionable ? onClick : undefined}
            className={cn(
                "p-4 rounded-2xl border flex flex-col justify-center transition-all duration-300",
                isActionable ? "cursor-pointer hover:border-primary/40 hover:bg-primary/5 hover:translate-y-[-2px] hover:shadow-lg active:scale-95" : "opacity-80",
                danger ? "bg-destructive/5 border-destructive/20" : "bg-muted/30 border-border/50"
            )}
        >
            <p className={cn(
                "text-[10px] uppercase tracking-widest font-black mb-1 flex items-center gap-1.5",
                danger ? "text-destructive" : "text-muted-foreground"
            )}>
                {danger && <AlertTriangle className="w-3.5 h-3.5" />} {title}
            </p>
            <p className={cn("text-3xl font-black", danger ? "text-destructive" : "text-foreground")}>{count}</p>
            <p className={cn("text-xs font-bold mt-1 opacity-70", danger ? "text-destructive" : "text-muted-foreground")}>{sub}</p>
        </div>
    );
}

function PulseStat({ label, value, danger, onClick }) {
    const isActionable = onClick && value > 0;
    return (
        <div
            onClick={isActionable ? onClick : undefined}
            className={cn(
                "flex flex-col p-4 rounded-2xl border transition-all duration-300 group",
                isActionable ? "cursor-pointer hover:border-primary/40 hover:bg-primary/5 hover:translate-y-[-2px] hover:shadow-lg active:scale-95" : "opacity-80 contrast-[0.8]",
                danger ? "bg-destructive/5 border-destructive/20" : "bg-muted/30 border-border/50"
            )}
        >
            <span className={cn(
                "text-[9px] font-black uppercase tracking-widest mb-1.5 transition-colors",
                danger ? "text-destructive" : "text-muted-foreground group-hover:text-primary"
            )}>{label}</span>
            <span className={cn(
                "text-3xl font-black tracking-tight",
                danger ? "text-destructive" : "text-foreground"
            )}>{value}</span>
        </div>
    );
}

const SpotlightCard = ({ icon: Icon, title, name, value, sub, color, bg }) => (
    <div className={cn(
        "p-5 rounded-3xl border border-border/50 flex items-start gap-4 transition-all duration-500 hover:scale-[1.02] hover:shadow-xl cursor-default group backdrop-blur-sm",
        bg
    )}>
        <div className="p-3 rounded-2xl bg-card shadow-sm border border-border/50 group-hover:border-primary/30 transition-colors">
            <Icon className={cn("w-5 h-5", color)} />
        </div>
        <div className="flex-1 min-w-0">
            <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-1.5 opacity-60">{title}</p>
            <p className="text-base font-black text-foreground truncate tracking-tight uppercase">{name || 'N/A'}</p>
            <div className="flex items-baseline gap-2 mt-1">
                <span className={cn("text-xl font-black", color)}>{value || '---'}</span>
                <span className="text-[10px] font-black text-muted-foreground uppercase tracking-tighter opacity-50 truncate">{sub}</span>
            </div>
        </div>
    </div>
);

const SectionError = ({ title, onRetry, description, retryLabel }) => (
    <div className="flex flex-col items-center justify-center p-12 bg-muted/20 border-2 border-dashed border-border/50 rounded-3xl text-center h-full min-h-[320px] backdrop-blur-sm">
        <div className="p-4 bg-amber-500/10 rounded-full mb-4 border border-amber-500/20">
            <AlertTriangle className="w-8 h-8 text-amber-500" />
        </div>
        <p className="text-base font-black text-foreground mb-1.5">{title}</p>
        <p className="text-xs text-muted-foreground mb-6 px-10 leading-relaxed font-bold opacity-70">
            {description}
        </p>
        {onRetry && (
            <Button
                variant="outline"
                size="sm"
                onClick={onRetry}
                className="font-black uppercase tracking-widest text-[10px] h-10 px-6 rounded-xl border-primary/20 hover:border-primary/40 hover:bg-primary/5 gap-2"
            >
                <RefreshCw className="w-3.5 h-3.5" /> {retryLabel}
            </Button>
        )}
    </div>
);

export default SalesDashboard;
